import {
  PendingTopup,
  TopupWithState,
  VALID_TOPUP_STATES,
} from '@/src/api/payg';
import { PAYG_EVENTS } from '@/src/constants/analytics';
import AnalyticsService from '@/src/services/analyticsService';
import React, { ComponentProps, useEffect } from 'react';
import { CodeBox } from '../../../CodeBox';
import { formatUtrn } from '../../../utils/formatUtrn';
import { TopupNotificationState } from '../../../utils/topupCopy';
import {
  getTopupNotificationCopy,
  isTopupNotification,
} from '../../../utils/topupCopyHelpers';
import { NotificationCard } from '../NotificationCard';

type FinalNotificationProps = {
  topup: TopupWithState<PendingTopup, VALID_TOPUP_STATES>;
  onDismiss: () => void;
};

type NotificationTopup = TopupWithState<PendingTopup, TopupNotificationState>;

const getCode = (
  topup: NotificationTopup,
): ComponentProps<typeof CodeBox> | null => {
  if ('utrn' in topup) {
    return {
      code: formatUtrn(topup.utrn),
      accessibilityLabel: 'Your top-up code is',
    };
  }
  if ('errors' in topup && topup.errors?.length) {
    return { code: topup.errors.map(({ code }) => code).join(', ') };
  }
  return null;
};

export const FinalNotification = ({
  topup,
  onDismiss,
}: FinalNotificationProps) => {
  useEffect(() => {
    AnalyticsService.dispatch(
      ...PAYG_EVENTS.postPaymentAlertDisplayed({
        state: topup.state,
        topupSessionId: topup.topupSessionId,
      }),
    );
  }, [topup.state, topup.topupSessionId]);

  const handleDismiss = () => {
    AnalyticsService.dispatch(
      ...PAYG_EVENTS.postPaymentAlertDismissed({
        state: topup.state,
        topupSessionId: topup.topupSessionId,
      }),
    );
    onDismiss();
  };

  if (!isTopupNotification(topup)) {
    return null;
  }

  const copy = getTopupNotificationCopy({
    state: topup.state,
    fuel: topup.fuel,
    isAsc: topup.topupType === 'asc',
  });
  return (
    <NotificationCard
      id={topup.state.toLowerCase()}
      variant={topup.state === 'SUCCESSFULLY_APPLIED' ? 'success' : 'error'}
      copy={copy}
      code={getCode(topup)}
      onDismiss={handleDismiss}
    />
  );
};
